import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { ClientStore } from '../features/client/domain/+state/client.store';
import { isPromiseTimeout, withTimeout } from '../features/projects/domain/utils/with-timeout.util';
import { activeClientSession, clearClientPreview, readClientPreview, readClientSession } from './client-session';
import { ACCESS_READ_LIMIT_MS, VerdictBudget } from './route-wait';

/**
 * ПИН-режим: пока у ученика нет своего ПИНа, обязательный шаг «придумай ПИН» на карточке нельзя
 * пропустить, поменяв адрес в строке браузера. Без гварда логин без ПИНа выдаёт обычную сессию,
 * и /gallery или /select открываются напрямую — карточка так и остаётся незаполненной.
 * Сбой и молчание сервера пропускают, как и раньше сбой: ПИН сверяет сервер, гвард — лишь шаг UX. Вердикт,
 * опоздавший к пределу, ведёт на карточку или вход позже — если ученик ещё там и это он же.
 */
export const clientPinEnrollmentGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const linkId: string | null = route.paramMap.get('linkId');
  if (!linkId) return true;

  // Просмотр «как клиент» привязан к своей ссылке: чужой адрес в этой же вкладке закрывает его,
  // иначе запись выдавала бы одного ученика за другого.
  const preview = readClientPreview();
  if (preview && preview.linkId !== linkId) clearClientPreview();
  // Предпросмотр ПИН не заводит: ключ от альбома придумывает сам ученик. Без этой развилки гвард
  // возвращал бы владельца на карточку без конца — «Продолжить» оттуда ведёт ровно сюда.
  else if (preview) return true;

  const router = inject(Router);
  const store = inject(ClientStore);
  const budget = inject(VerdictBudget);

  // Обложку могли опустошить, пока ученик внутри: вердикт перечитывается на каждом заходе. Токен —
  // до openLink (загрузка ссылки сбрасывает стор) или из сессии: вернуть ученика надо на его личную
  // ссылку, общая в режиме личных ссылок закрыта.
  const session = readClientSession();
  const token = store.tokenParam() ?? (session?.linkId === linkId ? session.token : undefined);
  const verdict = async (): Promise<boolean | UrlTree> => {
    await store.refreshCoverVerdict(linkId);
    // Ссылку, прочитанную перечитыванием выше, openLink второй раз не грузит.
    await store.openLink(linkId);
    if (store.coverLocked()) {
      if (token) return router.createUrlTree(['/client', 't', token]);
      // Вердикт прочитан этим переходом, редирект везёт его дальше: страница входа второго GET не делает.
      return router.createUrlTree(['/client', linkId]);
    }

    if (store.studentLoginMode() !== 'pin') return true;

    // Нет активной персоны — это не «ПИН не задан», а «ещё не вошёл»: пусть решают сами страницы.
    const person = store.activePerson();
    if (!person || person.pinSet) return true;

    return router.createUrlTree(['/client', linkId, 'contact']);
  };

  const student = activeClientSession();
  const pending = verdict();
  try {
    return await withTimeout(pending, ACCESS_READ_LIMIT_MS);
  } catch (error) {
    if (!isPromiseTimeout(error)) throw error;
    // Живой, но медленный сервер: чтения стора ограничены каждое своим пределом, и вердикт ещё приедет.
    budget.applyLate(
      pending.then(late => (typeof late === 'boolean' ? null : () => budget.redirect(late))),
      url => {
        const now = activeClientSession();
        return url === state.url && now?.personId === student?.personId && now?.linkId === student?.linkId;
      },
    );
    return true;
  }
};
